import type { ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import type { IconNames } from "@/v2-ui/icon/icon.types"

/**
 * Everything a sidebar entry looks like, independent of what it *is*.
 * SidebarLink (an anchor, for routes) and SidebarItem (a button, for
 * actions and popover triggers) both take these so the two read as one
 * component with two element forms.
 */
export type SidebarItemVisualProps = {
  isActive?: boolean
  isDisabled?: boolean
  isLoading?: boolean
  isChildrenHiddenOnMini?: boolean
  iconSrc?: IconNames
  iconClassName?: string
  tooltipContent?: ReactNode
  tooltipDisabled?: boolean
  /**
   * Trailing indicator (e.g. a count badge). Unlike children it survives the
   * mini sidebar: it renders at the item's right edge normally, and as an
   * absolutely positioned overlay on the icon when minimized.
   */
  badge?: ReactNode
}

/**
 * Class list for one sidebar entry. The classes live in sidebar.css (plain
 * selectors, single-class specificity), so callsite utilities still win by
 * cascade.
 */
export function getSidebarItemClasses(options: {
  isMini?: boolean
  isActive?: boolean
  isDisabled?: boolean
  className?: string
}) {
  const { isMini, isActive, isDisabled, className } = options

  return cn("sidebar-item nav-item", className, {
    "is-mini": isMini,
    "is-active": isActive,
    "is-disabled": isDisabled,
  })
}
